import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { subscribeToPushNotifications } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Bell,
  BellRing,
  CheckCheck,
  CheckCircle2,
  Clock,
  FileText,
  FileCheck,
  AlertTriangle,
  BookOpen,
  CalendarDays,
  Megaphone,
  Loader2,
  Smartphone,
} from "lucide-react";
import { toast } from "sonner";

export const NotificationCenter = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // Check current push notification permission
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "granted") {
      setPushEnabled(true);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.getNotificaciones();
      setNotifications(res.data || []);
      const unread = (res.data || []).filter((n) => !n.leida).length;
      setUnreadCount(unread);
    } catch (err) {
      console.error("Error cargando notificaciones:", err);
    }
  }, []);

  // Poll notifications every 30 seconds
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleEnablePush = async () => {
    setSubscribing(true);
    try {
      await subscribeToPushNotifications();
      setPushEnabled(true);
      toast.success("¡Notificaciones Push activadas!", {
        description: "Recibirás alertas en tiempo real en este dispositivo.",
      });
    } catch (err) {
      console.error("Error al activar push:", err);
      toast.error(err.message || "No se pudo activar las notificaciones push.");
    } finally {
      setSubscribing(false);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.marcarTodasLeidas();
      setNotifications((prev) => prev.map((n) => ({ ...n, leida: true })));
      setUnreadCount(0);
      toast.success("Todas las notificaciones marcadas como leídas");
    } catch (err) {
      toast.error("Error al actualizar notificaciones");
    }
  };

  const handleNotificationClick = async (notif) => {
    if (!notif.leida) {
      try {
        await api.marcarNotificacionLeida(notif.id);
        setNotifications((prev) =>
          prev.map((n) => (n.id === notif.id ? { ...n, leida: true } : n))
        );
        setUnreadCount((c) => Math.max(0, c - 1));
      } catch (e) {
        // ignore
      }
    }
    setOpen(false);
    if (notif.url_destino) {
      navigate(notif.url_destino);
    }
  };

  const getNotificationIcon = (tipo) => {
    switch (tipo) {
      case "solicitud":
      case "aprobacion":
      case "rechazo":
        return <CalendarDays className="w-4 h-4 text-orange-500" />;
      case "constancia":
        return <FileText className="w-4 h-4 text-blue-500" />;
      case "boleto_ornato":
        return <FileCheck className="w-4 h-4 text-emerald-500" />;
      case "llamada_atencion":
        return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      case "manual":
        return <BookOpen className="w-4 h-4 text-purple-500" />;
      case "comunicado":
        return <Megaphone className="w-4 h-4 text-rose-500" />;
      default:
        return <Bell className="w-4 h-4 text-slate-500" />;
    }
  };

  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffMin = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMin / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMin < 1) return "Justo ahora";
      if (diffMin < 60) return `Hace ${diffMin} min`;
      if (diffHours < 24) return `Hace ${diffHours} h`;
      if (diffDays === 1) return "Ayer";
      if (diffDays < 7) return `Hace ${diffDays} d`;
      return date.toLocaleDateString("es-GT");
    } catch {
      return "";
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-10 w-10 rounded-full hover:bg-slate-100"
          title="Notificaciones"
        >
          {unreadCount > 0 ? (
            <BellRing className="w-5 h-5 text-orange-600 animate-pulse" />
          ) : (
            <Bell className="w-5 h-5 text-slate-600" />
          )}
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1 bg-red-500 text-white text-[11px] font-bold rounded-full flex items-center justify-center border-2 border-white shadow-sm">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        className="w-[360px] sm:w-[420px] p-0 shadow-2xl rounded-2xl border-slate-200"
      >
        {/* Header */}
        <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/70 rounded-t-2xl">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-slate-900 text-base">Notificaciones</h3>
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 text-xs font-semibold bg-orange-100 text-orange-700 rounded-full">
                {unreadCount} nuevas
              </span>
            )}
          </div>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllRead}
              className="text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50 h-8 px-2"
            >
              <CheckCheck className="w-3.5 h-3.5 mr-1" />
              Marcar todas leídas
            </Button>
          )}
        </div>

        {/* Push status banner */}
        <div className="px-4 py-2.5 bg-gradient-to-r from-orange-50 to-amber-50 border-b border-orange-100 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-slate-700">
            <Smartphone className="w-4 h-4 text-orange-500 shrink-0" />
            <span>
              {pushEnabled
                ? "Notificaciones push activadas en este dispositivo"
                : "Activa notificaciones push para recibir alertas al instante"}
            </span>
          </div>
          {!pushEnabled && (
            <Button
              size="sm"
              variant="outline"
              disabled={subscribing}
              onClick={handleEnablePush}
              className="h-7 text-[11px] px-2.5 border-orange-300 text-orange-700 hover:bg-orange-100 shrink-0 font-medium"
            >
              {subscribing ? (
                <Loader2 className="w-3 h-3 animate-spin mr-1" />
              ) : null}
              Activar
            </Button>
          )}
        </div>

        {/* Notification List */}
        <div className="max-h-[380px] overflow-y-auto divide-y divide-slate-100">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-slate-300 stroke-1" />
              <p className="text-sm font-medium text-slate-600">Al día con todo</p>
              <p className="text-xs text-slate-400 mt-0.5">
                No tienes notificaciones pendientes
              </p>
            </div>
          ) : (
            notifications.map((n) => (
              <div
                key={n.id}
                onClick={() => handleNotificationClick(n)}
                className={`p-3.5 hover:bg-slate-50 transition-colors cursor-pointer flex gap-3 items-start ${
                  !n.leida ? "bg-orange-50/40" : ""
                }`}
              >
                <div
                  className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${
                    !n.leida ? "bg-white shadow-sm border border-orange-200" : "bg-slate-100"
                  }`}
                >
                  {getNotificationIcon(n.tipo)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p
                      className={`text-sm truncate ${
                        !n.leida ? "font-semibold text-slate-900" : "font-medium text-slate-700"
                      }`}
                    >
                      {n.titulo}
                    </p>
                    {!n.leida && (
                      <span className="w-2 h-2 rounded-full bg-orange-500 shrink-0 mt-1.5" />
                    )}
                  </div>
                  <p className="text-xs text-slate-600 line-clamp-2 mt-0.5 leading-relaxed">
                    {n.mensaje}
                  </p>
                  <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatRelativeTime(n.creado_en)}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="p-2 border-t border-slate-100 text-center bg-slate-50/50 rounded-b-2xl">
            <p className="text-[11px] text-slate-400">
              Mostrando las notificaciones más recientes
            </p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
